import { memo, ReactNode, useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import Button from '@atlaskit/button/new';
import ModalDialog, {
  ModalTitle,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@atlaskit/modal-dialog';

import { messages as diffMessages } from './diff.i18n';

type DialogConfig = {
  message: MessageDescriptor;
  onCancelDiscard?: () => void;
  onConfirmDiscard: () => void;
};

export const useDiscardCommentsDialogState = () => {
  const [config, setConfig] = useState<DialogConfig | null>(null);

  const confirmDiscardComments = useCallback(
    (cfg: DialogConfig) => setConfig(cfg),
    []
  );

  const onCancel = useCallback(() => {
    config?.onCancelDiscard?.();
    setConfig(null);
  }, [config]);

  const onDiscard = useCallback(() => {
    if (!config) {
      return;
    }
    config.onConfirmDiscard();
    setConfig(null);
  }, [config]);

  const dialogProps: Props = {
    children: config ? (
      <FormattedMessage {...config.message} tagName="p" />
    ) : null,
    onCancel,
    onDiscard,
  };

  return { confirmDiscardComments, dialogProps, isDialogOpen: !!config };
};

type Props = {
  children: ReactNode;
  onDiscard: () => void;
  onCancel: () => void;
};

const DiscardCommentsDialog = memo((props: Props) => {
  const intl = useIntl();
  const { onDiscard, onCancel, children } = props;

  return (
    <ModalDialog width="small" onClose={onCancel}>
      <ModalHeader>
        <ModalTitle appearance="danger">
          {intl.formatMessage(diffMessages.discardCommentsModalTitle)}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter>
        <Button appearance="danger" onClick={onDiscard}>
          {intl.formatMessage(diffMessages.discardCommentsModalDiscardButton)}
        </Button>
        <Button appearance="subtle" onClick={onCancel}>
          {intl.formatMessage(diffMessages.discardCommentsModalCancelButton)}
        </Button>
      </ModalFooter>
    </ModalDialog>
  );
});

// eslint-disable-next-line import/no-default-export
export default DiscardCommentsDialog;
